import '../tables.css';

import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../../recoil/atoms/userConfigState';
import { userSettingsState } from '../../../recoil/atoms/userSettingsState';
import { useAuthContext } from '../../../../common/contexts/useAuthContext';

import { translate } from '../../../../common/language/translations';
import { Toggle } from '../../controls/input/Toggle';
import { updateSeasonStatsToggles } from '../../../services/firestore/userSettings';
import { CareerTableClubCell, CareerTableNationalCell } from '../tableCells';
import { StringToAnyMap } from '../../../types/common';
import { careerTableStickyColumnWidth, getCareerTableColumns } from './careerColumns';


interface CareerTableProps {
  data: StringToAnyMap[];
  isNationalTeam: boolean;
  isGoalkeeper: boolean;
  widthUnit: number;
}

export const CareerTable: React.FC<CareerTableProps> = ({
  data,
  isNationalTeam,
  isGoalkeeper,
  widthUnit,
}) => {

  const { currentUser } = useAuthContext();

  const userConfig = useRecoilValue(userConfigState);
  const userSettings = useRecoilValue(userSettingsState);

  const seasonStatsToggles = useMemo(() => userSettings?.seasonStatsToggles ?? {}, [userSettings?.seasonStatsToggles]);

  const careerTableColumns = useMemo(() => getCareerTableColumns(isGoalkeeper), [isGoalkeeper]);


  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  const handleMouseEnter = (index: number) => !isNationalTeam ? setHoveredRowIndex(index) : null;
  const handleMouseLeave = () => setHoveredRowIndex(null);
  const handleRowClick = (index: number) => {
    if (!isNationalTeam) {
      setExpandedRows(prev => {
        const newSet = new Set(prev);
        if (newSet.has(index)) {
          newSet.delete(index);
        }
        else {
          newSet.add(index);
        }
        return newSet;
      });
    }
  };


  const [expandedRows, setExpandedRows] = useState<Set<number>>(
    seasonStatsToggles.showSeasonsInCareerTable ? new Set(data.map((_, index) => index)) : new Set()
  );


  const isRowExpanded = (index: number) => {
    return !isNationalTeam && expandedRows.has(index);
  };


  useEffect(() => {
    if (seasonStatsToggles.showSeasonsInCareerTable) {
      setExpandedRows(new Set(data.map((_, index) => index)));
    }
    else {
      setExpandedRows(new Set());
    }
  }, [seasonStatsToggles.showSeasonsInCareerTable, data]);


  return (
    <div
      className='table-container season-table-container'
      style={{ position: 'absolute', overflowX: widthUnit <= 1 ? 'auto' : 'hidden' }}
    >

      {/* Header */}
      <div className='table-header career-table-header season-table-header-background'>

        <div
          className='table-sticky-left season-table-header-background career-table-cell-with-border season-table-sticky-header-cell'
          style={{ width: careerTableStickyColumnWidth, paddingBottom: 0, paddingRight: 16 }}
        >
          {translate(isNationalTeam ? 'nationalTeamTitle' : 'clubTeamTitle', userConfig?.language)}

          {userConfig && !isNationalTeam && (
            <div
              className='flex-row align-center season-table-sticky-header-cell-toggle-text'
              title={translate('showAllSeasons', userConfig?.language)}
            >
              {translate('showSeasons', userConfig?.language)}
              <div className='table-header-toggle'>
                <Toggle
                  isToggled={seasonStatsToggles.showSeasonsInCareerTable ?? false}
                  setIsToggled={(value: boolean) => updateSeasonStatsToggles(
                    { 'showSeasonsInCareerTable': value },
                    userConfig.email,
                    userConfig.club,
                    currentUser
                  )}
                  isSmall={true}
                />
              </div>
            </div>
          )}
        </div>

        {careerTableColumns.map(column => {
          return (
            <div
              key={column.key}
              className='table-cell'
              style={{ width: column.width * widthUnit }}
            >
              <div style={{ paddingRight: column.paddingRight }}>
                {translate(column.key, userConfig?.language)}
              </div>
            </div>
          );
        })}
      </div>


      {/* Body */}
      <div>
        {data.map((row, rowIndex) => {

          const isExpanded = isRowExpanded(rowIndex);
          const isHovered = hoveredRowIndex === rowIndex;

          return (
            <div
              key={rowIndex}
              className={
                'table-row season-table-row career-table-row'
                + (isExpanded ? ' career-table-row-expanded' : rowIndex % 2 === 0 ? ' career-table-row-even' : ' career-table-row-odd')
                + (isHovered
                  ? (isExpanded ? ' career-table-row-expanded-hover' : rowIndex % 2 === 0 ? ' career-table-row-even-hover' : ' career-table-row-odd-hover')
                  : ''
                )
                + (isNationalTeam ? ' no-hover' : '')
              }
              style={{
                height: isNationalTeam ? 37 : 50 + (isExpanded ? (row.iterations.length * 34) + 1 : 0),
                zIndex: rowIndex,
              }}
            >

              <div
                className={
                  'table-sticky-left career-table-cell-with-border season-table-sticky-cell'
                  + (isExpanded ? ' career-table-row-expanded' : rowIndex % 2 === 0 ? ' season-table-sticky-cell-even' : ' season-table-sticky-cell-odd')
                  + (isHovered
                    ? (isExpanded ? ' career-table-row-expanded-hover' : rowIndex % 2 === 0 ? ' career-table-row-even-hover' : ' career-table-row-odd-hover')
                    : ''
                  )
                }
                style={{ width: careerTableStickyColumnWidth, overflow: 'hidden' }}
              >
                {isNationalTeam && <CareerTableNationalCell row={row} />}
                {!isNationalTeam && (
                  <CareerTableClubCell
                    row={row}
                    isExpanded={isExpanded}
                    onMouseEnter={() => handleMouseEnter(rowIndex)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleRowClick(rowIndex)}
                  />
                )}
              </div>


              {careerTableColumns.map(column => {
                return (
                  <div
                    key={column.key}
                    className='table-cell'
                    style={{ width: column.width * widthUnit, overflow: 'hidden' }}
                  >
                    {column.cell({
                      row,
                      isNationalTeam,
                      isExpanded,
                      onMouseEnter: () => handleMouseEnter(rowIndex),
                      onMouseLeave: handleMouseLeave,
                      onClick: () => handleRowClick(rowIndex),
                      language: userConfig?.language,
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

    </div>
  );
};
